import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, Phone, X, CalendarCheck, MessageCircle } from "lucide-react";
import { Logo } from "./Logo";
import { useSettings } from "../context/SettingsContext";
import { useModal } from "../context/ModalContext";
import { WhatsAppButton } from "./WhatsAppButton";

const NAV = [
  { label: "Services", to: "/#services" },
  { label: "Pricing", to: "/#pricing" },
  { label: "Areas", to: "/#areas" },
  { label: "Reviews", to: "/#reviews" },
  { label: "Contact", to: "/contact" },
];

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [openMenu, setOpenMenu] = useState(false);
  const { settings } = useSettings();
  const { open } = useModal();
  const navigate = useNavigate();
  const location = useLocation();
  const wa = settings.whatsapp || "https://wa.me/447956120510";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = openMenu ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [openMenu]);

  const go = (to) => {
    setOpenMenu(false);
    if (to.startsWith("/#")) {
      const id = to.slice(2);
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 350);
      } else {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate(to);
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 transition-[background,box-shadow,padding] duration-300 ${
        openMenu ? "z-[210]" : "z-[110]"
      } ${
        scrolled ? "glass border-b border-black/5 shadow-soft py-2" : "bg-white/80 backdrop-blur-md py-3"
      }`}
      data-testid="site-header"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 flex items-center justify-between gap-4">
        <Link to="/" className="site-header-logo min-w-0 shrink" onClick={() => window.scrollTo({ top: 0 })} data-testid="header-logo-link">
          <Logo size={36} />
        </Link>

        <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
          {NAV.map((n) => (
            <button
              key={n.label}
              onClick={() => go(n.to)}
              className="text-sm font-semibold text-neutral-700 hover:text-brand-red transition-colors"
              data-testid={`nav-${n.label.toLowerCase()}`}
            >
              {n.label}
            </button>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a href={`tel:${settings.phone_link || ""}`} className="flex items-center gap-2 text-sm font-bold text-neutral-900 hover:text-brand-red transition-colors" data-testid="header-phone" aria-label="Call us">
            <span className="brand-gradient text-white w-9 h-9 rounded-full flex items-center justify-center"><Phone size={16} /></span>
            <span className="hidden lg:inline">{settings.phone || "07956 120 510"}</span>
          </a>
          <WhatsAppButton label="WhatsApp" className="!px-4 !py-2.5 text-sm hidden lg:inline-flex" testId="header-whatsapp" size={16} />
          <button className="btn-primary text-sm" onClick={() => open("booking")} data-testid="header-book-btn">
            <CalendarCheck size={16} /> Book Lesson
          </button>
        </div>

        <button className="lg:hidden p-2.5 -mr-1 text-neutral-900 rounded-xl active:bg-neutral-100" onClick={() => setOpenMenu(true)} aria-label="Open menu" data-testid="mobile-menu-open">
          <Menu size={28} />
        </button>
      </div>

      <AnimatePresence>
        {openMenu && (
          <div className="fixed inset-0 z-[200] lg:hidden" style={{ isolation: "isolate" }}>
            <motion.div
              className="absolute inset-0"
              style={{ backgroundColor: "rgba(10,10,10,0.7)" }}
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setOpenMenu(false)}
            />
            <motion.div
              className="absolute right-0 top-0 h-full w-[86%] max-w-sm shadow-2xl flex flex-col"
              style={{ backgroundColor: "#ffffff", height: "100dvh" }}
              initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
              transition={{ type: "tween", ease: [0.22, 1, 0.36, 1], duration: 0.3 }}
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100 bg-white">
                <Logo size={32} />
                <button onClick={() => setOpenMenu(false)} aria-label="Close menu" className="p-2 rounded-xl active:bg-neutral-100" data-testid="mobile-menu-close"><X size={26} /></button>
              </div>
              <nav className="flex-1 flex flex-col px-4 py-2 overflow-y-auto bg-white" aria-label="Mobile">
                {NAV.map((n) => (
                  <button key={n.label} onClick={() => go(n.to)} className="text-left text-lg font-semibold py-4 px-2 border-b border-neutral-100 text-neutral-900 active:text-brand-red" data-testid={`mobile-nav-${n.label.toLowerCase()}`}>
                    {n.label}
                  </button>
                ))}
              </nav>
              <div className="flex flex-col gap-3 px-6 pt-5 pb-[calc(1.5rem+env(safe-area-inset-bottom))] border-t border-neutral-100 bg-white">
                <div className="grid grid-cols-2 gap-3">
                  <a href={`tel:${settings.phone_link || ""}`} className="btn-outline !px-4 !py-3" data-testid="mobile-call-btn"><Phone size={16} /> Call</a>
                  <a href={wa} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 rounded-full bg-[#25D366] text-white font-semibold px-4 py-3"><MessageCircle size={16} /> WhatsApp</a>
                </div>
                <button className="btn-primary" onClick={() => { setOpenMenu(false); open("booking"); }}><CalendarCheck size={16} /> Book Lesson</button>
                <button className="btn-dark" onClick={() => { setOpenMenu(false); open("quote"); }}>Get a Free Quote</button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </header>
  );
}
